/**
 * AriaSessionManager - Singleton class for global session management
 * 
 * This class manages the WebSocket connection, Anam client, and session state
 * outside of React's lifecycle, ensuring session continuity across page navigation.
 * 
 * Key features:
 * - Singleton pattern ensures only one instance exists globally
 * - Session persists across React component unmounts/remounts
 * - Proactive session refresh before expiration
 * - Tab visibility awareness for resource optimization
 * - Error recovery with exponential backoff
 */

import type { AnamClient } from '@anam-ai/js-sdk';
import type {
  AriaSDKConfig,
  SessionState,
  SessionResponse,
  SessionManagerStatus,
  SessionManagerEventType,
  SessionManagerListener,
  SessionManagerEvent,
  SessionRefreshConfig,
  AnamMessage,
  AnamMessageStreamEvent,
  ChatMessage,
  MessageHistoryEvent,
  MessageStreamEvent,
  SessionEndEvent,
  MessageHistoryAckResponse,
  MessageStreamAckResponse,
  SessionEndAckResponse,
  SessionEndErrorResponse,
  GenericAckResponse,
  MissingFieldsErrorResponse,
  WebSocketTriggerEvent,
  ClientToolEvent,
} from '@/types';

// ============================================================================
// Type Guards for Message Handling
// ============================================================================

function isSessionResponse(msg: unknown): msg is SessionResponse {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'status' in msg &&
    (msg.status === 'success' || msg.status === 'error') &&
    !('type' in msg)
  );
}

function isMessageHistoryAck(msg: unknown): msg is MessageHistoryAckResponse {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'type' in msg &&
    msg.type === 'message_history_ack' &&
    'status' in msg &&
    msg.status === 'ok'
  );
}

function isMessageStreamAck(msg: unknown): msg is MessageStreamAckResponse {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'type' in msg &&
    msg.type === 'message_stream_ack' &&
    'status' in msg &&
    msg.status === 'ok'
  );
}

function isSessionEndAck(msg: unknown): msg is SessionEndAckResponse {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'type' in msg &&
    msg.type === 'session_end_ack' &&
    'status' in msg &&
    msg.status === 'ok'
  );
}

function isSessionEndError(msg: unknown): msg is SessionEndErrorResponse {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'type' in msg &&
    msg.type === 'session_end_error' &&
    'status' in msg &&
    msg.status === 'error'
  );
}

function isWebSocketTriggerEvent(msg: unknown): msg is WebSocketTriggerEvent {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'type' in msg &&
    msg.type === 'trigger_event'
  );
}

function isGenericAck(msg: unknown): msg is GenericAckResponse {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'status' in msg &&
    msg.status === 'ok' &&
    'message' in msg &&
    !('type' in msg)
  );
}

function isMissingFieldsError(msg: unknown): msg is MissingFieldsErrorResponse {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    'status' in msg &&
    msg.status === 'error' &&
    'error' in msg &&
    msg.error === 'MISSING_FIELDS'
  );
}

// ============================================================================
// Default Configuration
// ============================================================================

const DEFAULT_REFRESH_CONFIG: SessionRefreshConfig = {
  bufferMinutes: 5,
  maxRetries: 3,
  baseRetryDelay: 1000,
  refreshOnlyWhenVisible: true,
};

// ============================================================================
// AriaSessionManager Singleton
// ============================================================================

export class AriaSessionManager {
  private static instance: AriaSessionManager | null = null;

  // Configuration
  private config: AriaSDKConfig;
  private refreshConfig: SessionRefreshConfig;

  // WebSocket connection
  private ws: WebSocket | null = null;

  // Anam client and streaming
  private anamClient: AnamClient | null = null;
  private videoStream: MediaStream | null = null;
  private audioInputStream: MediaStream | null = null;
  private hiddenVideoElement: HTMLVideoElement | null = null;
  private hiddenAudioElement: HTMLAudioElement | null = null;

  // Session state
  private sessionState: SessionState = {
    session_id: null,
    token: null,
    expires_at: null,
    isSessionReady: false,
    lastError: null,
    isPreloaded: false,
    isRefreshing: false,
  };

  // Manager status
  private status: SessionManagerStatus = 'idle';

  // Chat state
  private chatMessages: ChatMessage[] = [];
  private liveTranscript: string = '';
  private lastMessageHistory: AnamMessage[] = [];
  // Typed user turns sent via sendUserMessage(). Anam does NOT echo these back in
  // MESSAGE_HISTORY_UPDATED, so we track them here and re-merge on every rebuild.
  // anchorIndex = number of Anam-history messages present when the turn was typed,
  // so the rebuild can splice it back into chronological position instead of the tail.
  private typedUserMessages: Array<ChatMessage & { anchorIndex: number }> = [];
  // Length of the last Anam MESSAGE_HISTORY_UPDATED payload; used as the anchor above.
  private anamHistoryLength: number = 0;

  // Event listeners
  private listeners: Map<SessionManagerEventType, Set<SessionManagerListener>> = new Map();

  // Timers and retry state
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private retryCount: number = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  // Tab visibility
  private isTabVisible: boolean = true;
  private pendingRefresh: boolean = false;

  // Callback for WebSocket trigger events (to open assistant)
  private onWebSocketTrigger: ((event: WebSocketTriggerEvent) => void) | null = null;

  // Session persistence state
  private isPaused: boolean = false;
  private beforeUnloadHandler: (() => void) | null = null;

  // Lazy initialization flag - when true, Anam client is initialized only when widget opens
  private deferAnamInit: boolean = true;
  private isAnamInitializing: boolean = false;

  // Tool call state
  private isToolCallActive: boolean = false;
  private activeToolCall: ClientToolEvent | null = null;

  // Auto-reconnect state
  private autoReconnectEnabled: boolean = true;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isIntentionalDisconnect: boolean = false;

  // Auto-reconnect on Anam disconnect (for inactivity timeout)
  private isReconnecting: boolean = false;
  private autoReconnectOnAnamDisconnect: boolean = true;

  // Track if user has had a session before (to skip greeting on subsequent sessions)
  // This persists across session restarts within the same app lifecycle
  private hasHadSessionBefore: boolean = false;

  // ============================================================================
  // Singleton Pattern
  // ============================================================================

  private constructor(config: AriaSDKConfig) {
    this.config = config;
    this.refreshConfig = {
      ...DEFAULT_REFRESH_CONFIG,
      bufferMinutes: config.sessionRefreshBuffer ?? DEFAULT_REFRESH_CONFIG.bufferMinutes,
    };

    // Validate userId is provided (required for message storage)
    if (!config.userId) {
      console.warn('[AriaSessionManager] WARNING: userId not provided. Message storage will not work.');
    } else {
      console.log('[AriaSessionManager] Instance created with userId:', config.userId);
    }

    // Set up tab visibility listener
    this.setupVisibilityListener();

    // Set up beforeunload handler for session cleanup
    this.setupBeforeUnloadHandler();
  }

  /**
   * Get or create the singleton instance
   * 
   * If instance already exists and config has meaningful values (websocketUrl),
   * the config will be updated. Otherwise, existing config is preserved.
   */
  static getInstance(config: AriaSDKConfig): AriaSessionManager {
    if (!AriaSessionManager.instance) {
      AriaSessionManager.instance = new AriaSessionManager(config);
    } else if (config.websocketUrl) {
      // Only update config if it has meaningful values (not an empty object)
      // This prevents overwriting the config when getting existing instance
      AriaSessionManager.instance.updateConfig(config);
    }
    return AriaSessionManager.instance;
  }

  /**
   * Check if an instance exists
   */
  static hasInstance(): boolean {
    return AriaSessionManager.instance !== null;
  }

  /**
   * Destroy the singleton instance (for testing or cleanup)
   */
  static destroyInstance(): void {
    if (AriaSessionManager.instance) {
      AriaSessionManager.instance.destroy();
      AriaSessionManager.instance = null;
    }
  }

  // ============================================================================
  // Configuration
  // ============================================================================

  private updateConfig(config: AriaSDKConfig): void {
    const previousUserId = this.config.userId;
    this.config = config;
    this.refreshConfig = {
      ...this.refreshConfig,
      bufferMinutes: config.sessionRefreshBuffer ?? this.refreshConfig.bufferMinutes,
    };
    
    // Warn if userId changed or is missing
    if (!config.userId) {
      console.warn('[AriaSessionManager] WARNING: Config updated without userId. Message storage will not work.');
    } else if (previousUserId !== config.userId) {
      console.log('[AriaSessionManager] Config updated, userId changed:', previousUserId, '->', config.userId);
    }
  }

  getConfig(): AriaSDKConfig {
    return this.config;
  }

  /**
   * Check if the manager is properly configured for message storage
   * Returns true if both userId and WebSocket are available
   */
  canStoreMessages(): boolean {
    return !!(this.config.userId && this.ws && this.ws.readyState === WebSocket.OPEN);
  }

  /**
   * Get the current userId from config
   */
  getUserId(): string | undefined {
    return this.config.userId;
  }

  // ============================================================================
  // Event System
  // ============================================================================

  addEventListener(type: SessionManagerEventType, listener: SessionManagerListener): void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(listener);
  }

  removeEventListener(type: SessionManagerEventType, listener: SessionManagerListener): void {
    this.listeners.get(type)?.delete(listener);
  }

  private emit(event: SessionManagerEvent): void {
    const typeListeners = this.listeners.get(event.type);
    if (typeListeners) {
      typeListeners.forEach(listener => {
        try {
          listener(event);
        } catch (err) {
          console.error('[AriaSessionManager] Error in event listener:', err);
        }
      });
    }
  }

  // ============================================================================
  // Status Management
  // ============================================================================

  private setStatus(newStatus: SessionManagerStatus): void {
    if (this.status !== newStatus) {
      const oldStatus = this.status;
      this.status = newStatus;
      console.log(`[AriaSessionManager] Status changed: ${oldStatus} -> ${newStatus}`);
      this.emit({ type: 'status_change', data: { oldStatus, newStatus } });
    }
  }

  getStatus(): SessionManagerStatus {
    return this.status;
  }

  // ============================================================================
  // Session State
  // ============================================================================

  getSessionState(): SessionState {
    return { ...this.sessionState };
  }

  private updateSessionState(updates: Partial<SessionState>): void {
    this.sessionState = { ...this.sessionState, ...updates };
  }

  // ============================================================================
  // WebSocket Connection
  // ============================================================================

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[AriaSessionManager] WebSocket already connected');
      return;
    }

    if (this.ws?.readyState === WebSocket.CONNECTING) {
      console.log('[AriaSessionManager] WebSocket already connecting');
      return;
    }

    // Reset reconnect state for fresh connection
    this.resetReconnectState();
    this.setStatus('connecting');

    try {
      this.ws = new WebSocket(this.config.websocketUrl);

      this.ws.onopen = () => {
        console.log('[AriaSessionManager] WebSocket connected');
        this.setStatus('connected');
        this.retryCount = 0;
        this.emit({ type: 'connection_change', data: { connected: true } });
        this.config.onConnectionStateChange?.(true);

        // Auto-request session token on connect with user_trigger=true
        // This ensures we get a valid session token that can be used when user opens widget
        // The token is stored and the Anam client is initialized lazily when widget opens
        if (this.config.preloadSession !== false) {
          console.log('[AriaSessionManager] Requesting session token for user:', this.config.userId || '(no userId)');
          this.requestSessionToken();
        }
      };

      this.ws.onmessage = (event) => this.handleWebSocketMessage(event);

      this.ws.onerror = (error) => {
        console.error('[AriaSessionManager] WebSocket error:', error);
        this.setStatus('error');
        this.updateSessionState({ lastError: 'WebSocket connection error' });
        this.config.onError?.(new Error('WebSocket connection failed'));
      };

      this.ws.onclose = (event) => {
        console.log('[AriaSessionManager] WebSocket disconnected, code:', event.code);
        this.emit({ type: 'connection_change', data: { connected: false } });
        this.config.onConnectionStateChange?.(false);

        // Don't reset session state on close - we want to preserve it for reconnection
        // Only reset if we're not in a refresh cycle
        if (!this.sessionState.isRefreshing) {
          this.setStatus('idle');
        }

        // Auto-reconnect if enabled and not intentionally disconnected
        if (this.autoReconnectEnabled && !this.isIntentionalDisconnect && !this.isPaused) {
          this.scheduleReconnect();
        }
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect WebSocket';
      console.error('[AriaSessionManager] Connection error:', errorMessage);
      this.setStatus('error');
      this.updateSessionState({ lastError: errorMessage });
      this.config.onError?.(err instanceof Error ? err : new Error(errorMessage));
    }
  }

  disconnect(): void {
    // Mark as intentional disconnect to prevent auto-reconnect
    this.isIntentionalDisconnect = true;
    this.clearTimers();
    this.clearReconnectTimer();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.setStatus('idle');
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // ============================================================================
  // Auto-Reconnect Logic
  // ============================================================================

  /**
   * Enable or disable auto-reconnect functionality
   */
  setAutoReconnect(enabled: boolean): void {
    this.autoReconnectEnabled = enabled;
    if (!enabled) {
      this.clearReconnectTimer();
    }
  }

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[AriaSessionManager] Max reconnect attempts reached, giving up');
      this.setStatus('error');
      this.updateSessionState({ lastError: 'Connection lost - max reconnect attempts reached' });
      this.config.onError?.(new Error('WebSocket connection lost after max reconnect attempts'));
      return;
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s (max 30s)
    const baseDelay = 1000;
    const delay = Math.min(baseDelay * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;

    console.log(`[AriaSessionManager] Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

    this.reconnectTimer = setTimeout(() => {
      if (!this.isIntentionalDisconnect && !this.isPaused) {
        console.log(`[AriaSessionManager] Attempting reconnect (attempt ${this.reconnectAttempts})`);
        this.reconnect();
      }
    }, delay);
  }

  /**
   * Attempt to reconnect the WebSocket
   */
  private reconnect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[AriaSessionManager] Already connected, skipping reconnect');
      return;
    }

    if (this.ws?.readyState === WebSocket.CONNECTING) {
      console.log('[AriaSessionManager] Already connecting, skipping reconnect');
      return;
    }

    this.setStatus('connecting');

    try {
      this.ws = new WebSocket(this.config.websocketUrl);

      this.ws.onopen = () => {
        console.log('[AriaSessionManager] WebSocket reconnected successfully');
        this.setStatus('connected');
        this.reconnectAttempts = 0; // Reset on successful connection
        this.emit({ type: 'connection_change', data: { connected: true } });
        this.config.onConnectionStateChange?.(true);

        // If we have a valid session token, we're good to go
        // Otherwise, request a new session token
        if (this.sessionState.token && !this.isTokenExpired()) {
          console.log('[AriaSessionManager] Reconnected with existing valid session token');
          // If Anam client is initialized, we're ready
          if (this.anamClient) {
            this.setStatus('ready');
          }
          // Re-schedule refresh timer
          this.scheduleRefresh();
        } else if (this.config.preloadSession !== false && this.config.userId) {
          console.log('[AriaSessionManager] Reconnected, requesting new session token');
          this.requestSessionToken();
        }
      };

      this.ws.onmessage = (event) => this.handleWebSocketMessage(event);

      this.ws.onerror = (error) => {
        console.error('[AriaSessionManager] WebSocket reconnect error:', error);
        // Don't set error status here, let onclose handle reconnect scheduling
      };

      this.ws.onclose = (event) => {
        console.log('[AriaSessionManager] WebSocket disconnected during reconnect, code:', event.code);
        this.emit({ type: 'connection_change', data: { connected: false } });
        this.config.onConnectionStateChange?.(false);

        // Continue trying to reconnect
        if (this.autoReconnectEnabled && !this.isIntentionalDisconnect && !this.isPaused) {
          this.scheduleReconnect();
        }
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reconnect WebSocket';
      console.error('[AriaSessionManager] Reconnect error:', errorMessage);
      
      // Schedule another reconnect attempt
      if (this.autoReconnectEnabled && !this.isIntentionalDisconnect && !this.isPaused) {
        this.scheduleReconnect();
      }
    }
  }

  /**
   * Check if the current session token is expired
   */
  private isTokenExpired(): boolean {
    if (!this.sessionState.expires_at) {
      return false; // No expiry means token doesn't expire
    }
    const expiresAt = new Date(this.sessionState.expires_at).getTime();
    return Date.now() >= expiresAt;
  }

  /**
   * Check if the session token is valid and can be used
   * Public method for external checks
   */
  isSessionValid(): boolean {
    return this.sessionState.token !== null && !this.isTokenExpired();
  }

  /**
   * Get time until token expires in milliseconds
   * Returns -1 if no expiry or already expired
   */
  getTimeUntilExpiry(): number {
    if (!this.sessionState.expires_at) {
      return -1;
    }
    const expiresAt = new Date(this.sessionState.expires_at).getTime();
    const remaining = expiresAt - Date.now();
    return remaining > 0 ? remaining : -1;
  }

  /**
   * Clear the reconnect timer
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Reset reconnect state (call when intentionally connecting)
   */
  private resetReconnectState(): void {
    this.reconnectAttempts = 0;
    this.isIntentionalDisconnect = false;
    this.clearReconnectTimer();
  }

  // ============================================================================
  // WebSocket Message Handling
  // ============================================================================

  private handleWebSocketMessage(event: MessageEvent): void {
    try {
      const parsedData: unknown = JSON.parse(event.data);

      // Handle session response
      if (isSessionResponse(parsedData)) {
        this.handleSessionResponse(parsedData);
        return;
      }

      // Handle message history acknowledgment
      if (isMessageHistoryAck(parsedData)) {
        console.log(`[AriaSessionManager] Message history acknowledged: ${parsedData.message_count} messages`);
        this.config.onMessageHistoryAck?.(parsedData.message_count);
        this.emit({ type: 'message_history', data: parsedData });
        return;
      }

      // Handle message stream acknowledgment
      if (isMessageStreamAck(parsedData)) {
        console.log('[AriaSessionManager] Message stream acknowledged');
        this.config.onMessageStreamAck?.();
        this.emit({ type: 'message_stream', data: parsedData });
        return;
      }

      // Handle session end acknowledgment
      if (isSessionEndAck(parsedData)) {
        console.log(`[AriaSessionManager] Session end acknowledged: ${parsedData.saved_count} messages saved`);
        this.config.onSessionEndAck?.(parsedData.saved_count);
        return;
      }

      // Handle session end error
      if (isSessionEndError(parsedData)) {
        console.error(`[AriaSessionManager] Session end error: ${parsedData.message}`);
        this.config.onSessionEndError?.(parsedData.message, parsedData.error);
        this.config.onError?.(new Error(`Session end failed: ${parsedData.error} - ${parsedData.message}`));
        return;
      }

      // Handle missing fields error
      if (isMissingFieldsError(parsedData)) {
        console.error(`[AriaSessionManager] Missing fields error: ${parsedData.message}`);
        this.config.onError?.(new Error(parsedData.message));
        return;
      }

      // Handle WebSocket trigger events
      if (isWebSocketTriggerEvent(parsedData)) {
        console.log('[AriaSessionManager] WebSocket trigger event received:', parsedData.data);
        this.config.onWebSocketEvent?.(parsedData);
        this.onWebSocketTrigger?.(parsedData);
        return;
      }

      // Handle generic acknowledgments
      if (isGenericAck(parsedData)) {
        console.log(`[AriaSessionManager] Generic acknowledgment: ${parsedData.message}`);
        return;
      }

      // Legacy support for old session_token format
      if (
        typeof parsedData === 'object' &&
        parsedData !== null &&
        'type' in parsedData &&
        parsedData.type === 'session_token' &&
        'sessionToken' in parsedData &&
        typeof parsedData.sessionToken === 'string'
      ) {
        console.warn('[AriaSessionManager] Received legacy session_token format, converting');
        this.handleSessionResponse({
          status: 'success',
          message: 'Session created successfully (legacy format)',
          session_data: {
            session_id: 'legacy',
            token: parsedData.sessionToken,
            expires_at: new Date(Date.now() + 3600000).toISOString(),
          },
          time_taken: 0,
        });
        return;
      }

      console.warn('[AriaSessionManager] Received unknown message type:', parsedData);
    } catch (err) {
      if (err instanceof SyntaxError) {
        console.error('[AriaSessionManager] Failed to parse WebSocket message:', event.data);
        this.config.onError?.(new Error('Invalid JSON received from WebSocket'));
      } else {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        console.error('[AriaSessionManager] Error handling message:', errorMsg);
        this.config.onError?.(err instanceof Error ? err : new Error(errorMsg));
      }
    }
  }

  private handleSessionResponse(response: SessionResponse): void {
    const isRefreshing = this.sessionState.isRefreshing;

    if (response.status === 'success') {
      console.log('[AriaSessionManager] Session response:', response.message);

      if (response.session_data) {
        const sessionToken = response.session_data.sessionToken || response.session_data.token;

        if (!sessionToken) {
          console.error('[AriaSessionManager] No session token found in response');
          this.handleSessionError('No session token found in response');
          return;
        }

        console.log('[AriaSessionManager] Session token received successfully');
        console.log('[AriaSessionManager] Token expires at:', response.session_data.expires_at);

        this.updateSessionState({
          session_id: null, // Will be set when Anam client is ready
          token: sessionToken,
          expires_at: response.session_data.expires_at || null,
          isSessionReady: true,
          lastError: null,
          isRefreshing: false,
          isPreloaded: true, // Token is ready for use
        });

        // Set up proactive refresh timer based on expiration
        this.scheduleRefresh();

        // LAZY INITIALIZATION: Token is stored, Anam client will be initialized when widget opens
        if (this.deferAnamInit) {
          if (isRefreshing) {
            // Token was refreshed - if Anam client is already running, it continues to work
            // The new token will be used for the next session
            console.log('[AriaSessionManager] Session token refreshed proactively');
            this.config.onSessionRefresh?.();
            this.emit({ type: 'session_refresh', data: { token: sessionToken } });
            // Keep status as ready if Anam client is initialized
            if (this.anamClient) {
              this.setStatus('ready');
            } else {
              this.setStatus('connected');
            }
          } else if (this.isReconnecting) {
            // Token received during auto-reconnect - initialize Anam client immediately
            console.log('[AriaSessionManager] Token received during reconnect, initializing Anam client');
            this.initializeAnamClient(sessionToken)
              .then(() => {
                console.log('[AriaSessionManager] Anam client initialized after reconnect');
                this.isReconnecting = false;
                this.emit({ type: 'reconnecting', data: { isReconnecting: false } });
              })
              .catch((err) => {
                console.error('[AriaSessionManager] Failed to initialize Anam client during reconnect:', err);
                this.isReconnecting = false;
                this.emit({ type: 'reconnecting', data: { isReconnecting: false } });
                this.setStatus('error');
                this.updateSessionState({ lastError: 'Failed to reconnect session' });
              });
          } else {
            // Initial token received - ready for user to open widget
            console.log('[AriaSessionManager] Session token ready, waiting for user to open widget');
            this.setStatus('connected');
            this.emit({ type: 'session_ready', data: { sessionId: null, tokenReady: true, lazyInit: true } });
            this.config.onSessionPreloaded?.();
          }
        } else {
          // Non-lazy mode: Initialize Anam client immediately (old behavior)
          if (!this.anamClient) {
            this.initializeAnamClient(sessionToken);
          } else if (isRefreshing) {
            console.log('[AriaSessionManager] Session refreshed, token updated');
            this.config.onSessionRefresh?.();
            this.emit({ type: 'session_refresh', data: { token: sessionToken } });
          }
        }
      } else {
        console.log('[AriaSessionManager] Session response without data:', response.message);
        // Don't treat this as an error - the backend might be acknowledging something else
        this.updateSessionState({
          isRefreshing: false,
        });
      }
    } else {
      this.handleSessionError(`${response.error}: ${response.message}`);
    }
  }

  private handleSessionError(errorMsg: string): void {
    console.error('[AriaSessionManager] Session error:', errorMsg);

    this.updateSessionState({
      isSessionReady: false,
      lastError: errorMsg,
      isRefreshing: false,
    });

    this.setStatus('error');
    this.emit({ type: 'session_error', error: new Error(errorMsg) });
    this.config.onError?.(new Error(errorMsg));

    // Attempt retry with exponential backoff
    this.scheduleRetry();
  }

  // ============================================================================
  // Session Token Request (on component mount)
  // ============================================================================

  /**
   * Request a session token from the backend with user_trigger=true
   * This is called automatically when WebSocket connects
   * The token is stored and used when user opens the widget for the first time
   * 
   * If user has had a session before (in this app lifecycle), skip_greeting is sent
   * to prevent the greeting from playing again on subsequent sessions.
   */
  private requestSessionToken(): void {
    // Don't request if session token already exists and is valid
    if (this.sessionState.token && !this.isTokenExpired()) {
      console.log('[AriaSessionManager] Valid session token already exists, skipping request');
      return;
    }

    // Don't request if already in progress
    if (this.status === 'preloading') {
      console.log('[AriaSessionManager] Session request already in progress');
      return;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[AriaSessionManager] Cannot request session token: WebSocket not connected');
      return;
    }

    // Skip greeting if user has already had a session in this app lifecycle
    const shouldSkipGreeting = this.hasHadSessionBefore;
    
    console.log('[AriaSessionManager] Requesting session token with user_trigger=true for user:', this.config.userId, 
      shouldSkipGreeting ? '(skip_greeting: true)' : '(with greeting)');
    this.setStatus('preloading');

    try {
      // Send user_trigger: true to get a valid session token
      // Include userId for backends that need it
      // Include skip_greeting if user has had a session before
      const message = JSON.stringify({ 
        user_trigger: true,
        ...(this.config.userId && { userId: this.config.userId }),
        ...(shouldSkipGreeting && { skip_greeting: true })
      });
      console.log('[AriaSessionManager] Sending message:', message);
      this.ws.send(message);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to request session token';
      console.error('[AriaSessionManager] Session token request error:', errorMessage);
      this.handleSessionError(errorMessage);
    }
  }


  /**
   * Manually trigger a session (for user-initiated actions)
   * 
   * NOTE: With global session management, this is typically not needed.
   * The session token is automatically requested when the provider mounts.
   * This method will request a new token if one doesn't exist or is expired.
   */
  triggerSession(userTrigger: boolean = true): void {
    // If session token already exists and is valid, don't request a new one
    if (this.sessionState.token && !this.isTokenExpired()) {
      console.log('[AriaSessionManager] Valid session token already exists, skipping trigger');
      return;
    }

    // If we're already in a preloading state, don't trigger
    if (this.status === 'preloading') {
      console.log('[AriaSessionManager] Session request already in progress, skipping trigger');
      return;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('[AriaSessionManager] Cannot trigger session: WebSocket not connected');
      return;
    }

    // Skip greeting if user has already had a session in this app lifecycle
    const shouldSkipGreeting = this.hasHadSessionBefore;

    try {
      // Always use user_trigger: true with userId to ensure we get a valid session token
      // Include skip_greeting if user has had a session before
      const message = JSON.stringify({ 
        user_trigger: true,
        userId: this.config.userId,
        ...(shouldSkipGreeting && { skip_greeting: true })
      });
      this.ws.send(message);
      console.log('[AriaSessionManager] Sent session request with user_trigger: true, userId:', this.config.userId,
        shouldSkipGreeting ? '(skip_greeting: true)' : '(with greeting)');

      if (userTrigger && this.config.userId) {
        this.config.onManualTrigger?.(this.config.userId);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to trigger session';
      console.error('[AriaSessionManager] Trigger error:', errorMessage);
      this.config.onError?.(err instanceof Error ? err : new Error(errorMessage));
    }
  }

  // ============================================================================
  // Anam Client Management
  // ============================================================================

  /**
   * Check if Anam client is initialized
   */
  isAnamClientInitialized(): boolean {
    return this.anamClient !== null;
  }

  /**
   * Check if session token is available (ready for on-demand initialization)
   */
  hasSessionToken(): boolean {
    return this.sessionState.token !== null;
  }

  /**
   * Initialize the Anam client on demand (called when widget opens)
   * This is the lazy initialization entry point - ensures greeting plays when user opens widget
   */
  async initializeSessionOnDemand(): Promise<void> {
    // Already initialized
    if (this.anamClient) {
      console.log('[AriaSessionManager] Anam client already initialized');
      return;
    }

    // Already initializing
    if (this.isAnamInitializing) {
      console.log('[AriaSessionManager] Anam client initialization already in progress');
      return;
    }

    // Check if token is expired and refresh if needed
    if (this.sessionState.token && this.isTokenExpired()) {
      console.log('[AriaSessionManager] Session token expired, requesting new token');
      // Clear the expired token
      this.updateSessionState({ token: null, isSessionReady: false });
      // Request a new token
      this.requestSessionToken();
      throw new Error('Session token expired, requesting new token');
    }

    // No token available
    if (!this.sessionState.token) {
      console.error('[AriaSessionManager] Cannot initialize on demand: no session token');
      throw new Error('No session token available');
    }

    console.log('[AriaSessionManager] Initializing Anam client on demand (user opened widget)');
    await this.initializeAnamClient(this.sessionState.token);
  }

  private async initializeAnamClient(sessionToken: string): Promise<void> {
    if (this.anamClient) {
      console.log('[AriaSessionManager] Anam client already initialized');
      return;
    }

    if (this.isAnamInitializing) {
      console.log('[AriaSessionManager] Anam client initialization already in progress');
      return;
    }

    this.isAnamInitializing = true;
    console.log('[AriaSessionManager] Initializing Anam client');
    this.setStatus('preloading');

    try {
      // Create hidden video element for preloading
      this.createHiddenVideoElement();

      // Dynamically import the Anam SDK (including AnamEvent)
      const { createClient, AnamEvent } = await import('@anam-ai/js-sdk');
      const client = createClient(sessionToken);

      // Set up event listeners
      // Note: SESSION_READY fires when Anam session is established, but video stream
      // may not be available yet. We'll emit our own session_ready event after stream() returns.
      client.addListener(AnamEvent.SESSION_READY, () => {
        const anamSessionId = client.getActiveSessionId();

        if (anamSessionId) {
          this.updateSessionState({ session_id: anamSessionId });
          console.log('[AriaSessionManager] Anam session ready with ID:', anamSessionId);
        }
        
        // Don't emit session_ready here - wait until video stream is available
        // The session_ready event will be emitted after client.stream() completes
      });

      client.addListener(AnamEvent.CONNECTION_CLOSED, () => {
        console.log('[AriaSessionManager] Anam connection closed');
        this.emit({ type: 'connection_change', data: { connected: false } });
        
        // If paused, clear the stale session so a new one is created on resume
        if (this.isPaused) {
          console.log('[AriaSessionManager] Anam disconnected while paused, clearing stale session');
          this.clearStaleSessionForResume();
          return;
        }
        
        // Auto-reconnect if widget is open:
        // 1. Auto-reconnect on Anam disconnect is enabled
        // 2. Not intentionally disconnected (user didn't close widget)
        // 3. Not already reconnecting
        if (this.autoReconnectOnAnamDisconnect && 
            !this.isIntentionalDisconnect && 
            !this.isReconnecting) {
          console.log('[AriaSessionManager] Anam disconnected unexpectedly, auto-reconnecting...');
          this.autoReconnectSession();
        }
      });

      client.addListener(AnamEvent.MESSAGE_HISTORY_UPDATED, (messages) => {
        // Ensure we have session_id before sending (may arrive before SESSION_READY event)
        if (!this.sessionState.session_id) {
          const anamSessionId = client.getActiveSessionId();
          if (anamSessionId) {
            this.updateSessionState({ session_id: anamSessionId });
            console.log('[AriaSessionManager] Got session ID from message history event:', anamSessionId);
          }
        }

        this.anamHistoryLength = messages.length;
        this.chatMessages = messages.map(msg => ({
          role: msg.role as 'user' | 'persona',
          content: msg.content,
          timestamp: Date.now(),
        }));

        // Re-merge typed user turns Anam omits from history. Splice each at its
        // anchor (Anam-history length when typed) so it lands in chronological
        // order rather than after later persona replies. `inserted` offsets the
        // anchor for turns already spliced (anchors are non-decreasing by time).
        // Skip any whose content Anam now echoes, to avoid duplicates.
        const historyContent = new Set(this.chatMessages.map(m => m.content));
        let inserted = 0;
        for (const typed of this.typedUserMessages) {
          if (historyContent.has(typed.content)) continue;
          const pos = Math.min(typed.anchorIndex + inserted, this.chatMessages.length);
          this.chatMessages.splice(pos, 0, typed);
          inserted++;
        }

        const anamMessages: AnamMessage[] = messages.map(msg => ({
          role: msg.role as 'user' | 'persona',
          content: msg.content,
          timestamp: new Date().toISOString(),
        }));

        this.sendMessageHistory(anamMessages);
        this.emit({ type: 'message_history', data: { messages: this.chatMessages } });
      });

      client.addListener(AnamEvent.MESSAGE_STREAM_EVENT_RECEIVED, (event: AnamMessageStreamEvent) => {
        // Ensure we have session_id before sending (may arrive before SESSION_READY event)
        if (!this.sessionState.session_id) {
          const anamSessionId = client.getActiveSessionId();
          if (anamSessionId) {
            this.updateSessionState({ session_id: anamSessionId });
            console.log('[AriaSessionManager] Got session ID from message stream event:', anamSessionId);
          }
        }

        if (event.role === 'persona') {
          this.liveTranscript += event.content;
        } else if (event.role === 'user') {
          this.liveTranscript = '';
        }

        const anamMessage: AnamMessage = {
          role: event.role,
          content: event.content,
          timestamp: new Date().toISOString(),
        };
        this.sendMessageStream(anamMessage, event.is_final ?? false);
        this.emit({ type: 'message_stream', data: { event, liveTranscript: this.liveTranscript } });
      });

      // Listen for tool call events
      client.addListener(AnamEvent.CLIENT_TOOL_EVENT_RECEIVED, (toolEvent: ClientToolEvent) => {
        console.log('[AriaSessionManager] Tool call event received:', toolEvent.eventName);
        this.isToolCallActive = true;
        this.activeToolCall = toolEvent;

        // end_call is a deliberate session termination. Mark it intentional so the
        // CONNECTION_CLOSED handler below does not auto-reconnect the closed session.
        if (toolEvent.eventName === 'end_call') {
          console.log('[AriaSessionManager] end_call tool invoked, suppressing auto-reconnect');
          this.isIntentionalDisconnect = true;
          this.clearReconnectTimer();
        }
        this.emit({ 
          type: 'tool_call', 
          data: { 
            isActive: true, 
            toolName: toolEvent.eventName,
            toolData: toolEvent.eventData,
            toolEvent 
          } 
        });

        // Auto-clear tool call state after a timeout (tool calls should complete)
        // This is a fallback in case we don't receive a completion signal
        setTimeout(() => {
          if (this.activeToolCall?.eventUid === toolEvent.eventUid) {
            this.clearToolCallState();
          }
        }, 30000); // 30 second timeout
      });

      this.anamClient = client;

      // Get user microphone input
      this.audioInputStream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Create hidden audio element for proper audio playback
      this.createHiddenAudioElement();

      // Use stream() to get the video/audio stream, then handle audio separately
      // The stream() method returns [videoStream, audioStream]
      const streams = await client.stream(this.audioInputStream);
      const videoStream = streams[0];
      const audioStream = streams[1]; // Audio stream from Anam
      
      this.videoStream = videoStream;

      // Attach video stream to hidden video element (for preloading)
      if (this.hiddenVideoElement) {
        this.hiddenVideoElement.srcObject = videoStream;
        this.hiddenVideoElement.muted = true; // Video element stays muted
      }
      
      // Attach audio stream to hidden audio element for proper audio playback
      // This is the key fix: audio comes through a separate stream that we play through an audio element
      if (this.hiddenAudioElement && audioStream) {
        this.hiddenAudioElement.srcObject = audioStream;
        // Audio element is NOT muted - this is where Aria's voice comes from
        this.hiddenAudioElement.muted = false;
        this.hiddenAudioElement.play().catch((err) => {
          console.warn('[AriaSessionManager] Initial audio play failed (may need user interaction):', err);
        });
        console.log('[AriaSessionManager] Audio stream attached to audio element');
      } else {
        console.log('[AriaSessionManager] No separate audio stream available, audio may come through video');
      }

      // NOW emit session_ready - video stream is available
      // This is the correct time to notify the UI that it can attach the video element
      this.isAnamInitializing = false;
      this.setStatus('ready');
      const anamSessionId = client.getActiveSessionId();
      console.log('[AriaSessionManager] Session fully ready with video stream, ID:', anamSessionId);
      this.emit({ type: 'session_ready', data: { sessionId: anamSessionId, streamReady: true } });
      this.config.onSessionReady?.();

      // Mark that user has had a session - subsequent sessions will skip greeting
      if (!this.hasHadSessionBefore) {
        this.hasHadSessionBefore = true;
        console.log('[AriaSessionManager] First session started - subsequent sessions will skip greeting');
      }

      console.log('[AriaSessionManager] Anam client initialized and streaming');
    } catch (err) {
      this.isAnamInitializing = false;
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Anam client';
      console.error('[AriaSessionManager] Anam client error:', errorMessage);
      this.setStatus('error');
      this.updateSessionState({ lastError: errorMessage });
      this.config.onError?.(err instanceof Error ? err : new Error(errorMessage));
      throw err;
    }
  }

  /**
   * Clear tool call state
   */
  private clearToolCallState(): void {
    if (this.isToolCallActive) {
      this.isToolCallActive = false;
      this.activeToolCall = null;
      this.emit({ type: 'tool_call', data: { isActive: false, toolName: null, toolData: null } });
    }
  }

  /**
   * Get current tool call state
   */
  getToolCallState(): { isActive: boolean; toolName: string | null; toolData: Record<string, unknown> | null } {
    return {
      isActive: this.isToolCallActive,
      toolName: this.activeToolCall?.eventName ?? null,
      toolData: this.activeToolCall?.eventData ?? null,
    };
  }

  private createHiddenVideoElement(): void {
    if (this.hiddenVideoElement) return;

    this.hiddenVideoElement = document.createElement('video');
    this.hiddenVideoElement.autoplay = true;
    this.hiddenVideoElement.playsInline = true;
    this.hiddenVideoElement.muted = true; // Video is muted, audio comes from separate audio element
    this.hiddenVideoElement.style.position = 'absolute';
    this.hiddenVideoElement.style.width = '1px';
    this.hiddenVideoElement.style.height = '1px';
    this.hiddenVideoElement.style.opacity = '0';
    this.hiddenVideoElement.style.pointerEvents = 'none';
    this.hiddenVideoElement.style.zIndex = '-9999';

    document.body.appendChild(this.hiddenVideoElement);
    console.log('[AriaSessionManager] Hidden video element created');
  }

  private createHiddenAudioElement(): void {
    if (this.hiddenAudioElement) return;

    this.hiddenAudioElement = document.createElement('audio');
    this.hiddenAudioElement.autoplay = true;
    // Audio element is NOT muted - this is where Aria's voice comes from
    // The Anam SDK handles audio through this element, ensuring proper playback
    this.hiddenAudioElement.style.position = 'absolute';
    this.hiddenAudioElement.style.width = '1px';
    this.hiddenAudioElement.style.height = '1px';
    this.hiddenAudioElement.style.opacity = '0';
    this.hiddenAudioElement.style.pointerEvents = 'none';
    this.hiddenAudioElement.style.zIndex = '-9999';

    document.body.appendChild(this.hiddenAudioElement);
    console.log('[AriaSessionManager] Hidden audio element created');
  }

  private removeHiddenVideoElement(): void {
    if (this.hiddenVideoElement) {
      this.hiddenVideoElement.srcObject = null;
      this.hiddenVideoElement.remove();
      this.hiddenVideoElement = null;
    }
  }

  private removeHiddenAudioElement(): void {
    if (this.hiddenAudioElement) {
      this.hiddenAudioElement.srcObject = null;
      this.hiddenAudioElement.pause();
      this.hiddenAudioElement.remove();
      this.hiddenAudioElement = null;
    }
  }

  // ============================================================================
  // Video Stream Management
  // ============================================================================

  /**
   * Get the current video stream
   */
  getVideoStream(): MediaStream | null {
    return this.videoStream;
  }

  /**
   * Attach the video stream to a visible video element
   * Audio is handled by the hidden audio element if available, otherwise through video element
   */
  attachToVideoElement(element: HTMLVideoElement): void {
    if (this.videoStream) {
      // Log track status for debugging (but don't block attachment)
      const tracks = this.videoStream.getTracks();
      const activeTracks = tracks.filter(track => track.readyState === 'live');
      const audioTracks = tracks.filter(track => track.kind === 'audio');
      console.log('[AriaSessionManager] Attaching video stream, tracks:', tracks.length, 'active:', activeTracks.length, 'audio:', audioTracks.length);
      
      element.srcObject = this.videoStream;
      
      // Check if we have a separate audio element with audio
      const hasAudioElement = this.hiddenAudioElement && this.hiddenAudioElement.srcObject;
      
      if (hasAudioElement) {
        // Audio comes from separate audio element - keep video muted
        element.muted = true;
        // Ensure audio element is playing and not muted
        this.hiddenAudioElement!.muted = false;
        this.hiddenAudioElement!.play().catch((err) => {
          console.warn('[AriaSessionManager] Audio play failed:', err);
        });
        console.log('[AriaSessionManager] Audio playing through separate audio element');
      } else {
        // No separate audio element - audio comes through video element
        element.muted = false;
        console.log('[AriaSessionManager] Audio playing through video element');
      }
      
      // Ensure the video plays (important when re-attaching after pause/resume or minimize/maximize)
      element.play().catch((err) => {
        // Autoplay might be blocked, but usually works since user has interacted
        console.warn('[AriaSessionManager] Video play failed:', err);
      });
    } else {
      // No video stream - this is expected during session initialization or after session ended
      // Don't log as warning during normal operation
      console.log('[AriaSessionManager] No video stream available to attach (session may be initializing)');
    }
  }

  /**
   * Detach video stream from visible element (when widget closes)
   */
  detachFromVideoElement(element: HTMLVideoElement): void {
    element.srcObject = null;
    
    // Keep stream on hidden element
    if (this.hiddenVideoElement && this.videoStream) {
      this.hiddenVideoElement.srcObject = this.videoStream;
      this.hiddenVideoElement.muted = true;
    }
    
    // Mute audio when widget closes (but keep it attached for quick resume)
    if (this.hiddenAudioElement) {
      this.hiddenAudioElement.muted = true;
      console.log('[AriaSessionManager] Audio element muted (widget closed)');
    }
    
    console.log('[AriaSessionManager] Video stream detached from visible element');
  }

  // ============================================================================
  // Chat State
  // ============================================================================

  getChatMessages(): ChatMessage[] {
    return [...this.chatMessages];
  }

  getLiveTranscript(): string {
    return this.liveTranscript;
  }

  clearLiveTranscript(): void {
    this.liveTranscript = '';
  }

  // ============================================================================
  // Message Sending
  // ============================================================================

  private sendMessageHistory(messages: AnamMessage[], force: boolean = false): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.log('[AriaSessionManager] Cannot send message history: WebSocket not connected');
      return;
    }
    if (!this.sessionState.session_id) {
      console.log('[AriaSessionManager] Cannot send message history: no session_id');
      return;
    }
    if (!this.config.userId) {
      console.log('[AriaSessionManager] Cannot send message history: no userId');
      return;
    }

    if (!force && JSON.stringify(messages) === JSON.stringify(this.lastMessageHistory)) {
      return;
    }

    this.lastMessageHistory = messages;

    const payload: MessageHistoryEvent = {
      type: 'message_history',
      session_id: this.sessionState.session_id,
      user_id: this.config.userId,
      messages,
    };

    try {
      this.ws.send(JSON.stringify(payload));
      console.log('[AriaSessionManager] Sent message history:', messages.length, 'messages to session:', this.sessionState.session_id);
    } catch (err) {
      console.error('[AriaSessionManager] Failed to send message history:', err);
    }
  }

  private sendMessageStream(message: AnamMessage, is_final: boolean): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.log('[AriaSessionManager] Cannot send message stream: WebSocket not connected');
      return;
    }
    if (!this.sessionState.session_id) {
      console.log('[AriaSessionManager] Cannot send message stream: no session_id');
      return;
    }
    if (!this.config.userId) {
      console.log('[AriaSessionManager] Cannot send message stream: no userId');
      return;
    }

    const payload: MessageStreamEvent = {
      type: 'message_stream',
      session_id: this.sessionState.session_id,
      user_id: this.config.userId,
      message: { ...message, is_final },
    };

    try {
      this.ws.send(JSON.stringify(payload));
      if (is_final) {
        console.log('[AriaSessionManager] Sent final message stream:', message.role, 'to session:', this.sessionState.session_id);
      }
    } catch (err) {
      console.error('[AriaSessionManager] Failed to send message stream:', err);
    }
  }

  sendSessionEnd(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.log('[AriaSessionManager] Cannot send session end: WebSocket not connected');
      return;
    }
    if (!this.sessionState.session_id) {
      console.log('[AriaSessionManager] Cannot send session end: no session_id');
      return;
    }
    if (!this.config.userId) {
      console.log('[AriaSessionManager] Cannot send session end: no userId');
      return;
    }

    // Send final message history before ending session
    if (this.lastMessageHistory.length > 0) {
      console.log('[AriaSessionManager] Sending final message history before session end:', this.lastMessageHistory.length, 'messages');
      this.sendMessageHistory(this.lastMessageHistory, true);
    }

    const payload: SessionEndEvent = {
      type: 'session_end',
      session_id: this.sessionState.session_id,
      user_id: this.config.userId,
    };

    try {
      this.ws.send(JSON.stringify(payload));
      console.log('[AriaSessionManager] Sent session end event for session:', this.sessionState.session_id);
    } catch (err) {
      console.error('[AriaSessionManager] Failed to send session end:', err);
    }
  }

  // ============================================================================
  // Anam Client Actions
  // ============================================================================

  async sendMessage(message: string): Promise<void> {
    if (!this.anamClient) {
      throw new Error('Anam client not initialized');
    }

    try {
      // Route typed text through the brain as a user turn (talk() would just make
      // the avatar speak the text verbatim, bypassing the LLM and mislabeling it as persona).
      this.anamClient.sendUserMessage(message);

      // Anam does not emit a user-side event for typed messages, so record it
      // locally and show it immediately. Tracked in typedUserMessages so the next
      // MESSAGE_HISTORY_UPDATED rebuild (which replaces chatMessages wholesale)
      // re-merges it instead of dropping it.
      const typedMessage: ChatMessage & { anchorIndex: number } = {
        role: 'user',
        content: message,
        timestamp: Date.now(),
        anchorIndex: this.anamHistoryLength,
      };
      this.typedUserMessages.push(typedMessage);
      this.chatMessages.push(typedMessage);
      this.emit({ type: 'message_history', data: { messages: this.chatMessages } });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      this.config.onError?.(err instanceof Error ? err : new Error(errorMessage));
      throw err;
    }
  }

  toggleMute(): boolean {
    if (!this.anamClient) return false;

    const currentState = this.anamClient.getInputAudioState();
    if (currentState.isMuted) {
      this.anamClient.unmuteInputAudio();
      return false;
    } else {
      this.anamClient.muteInputAudio();
      return true;
    }
  }

  isMuted(): boolean {
    return this.anamClient?.getInputAudioState().isMuted ?? false;
  }

  async stopStreaming(): Promise<void> {
    if (this.anamClient) {
      try {
        await this.anamClient.stopStreaming();
      } catch (err) {
        console.error('[AriaSessionManager] Error stopping streaming:', err);
      }
      this.anamClient = null;
    }

    if (this.audioInputStream) {
      this.audioInputStream.getTracks().forEach(track => track.stop());
      this.audioInputStream = null;
    }

    this.videoStream = null;
    this.chatMessages = [];
    this.liveTranscript = '';
    this.lastMessageHistory = [];
    this.typedUserMessages = [];
    this.anamHistoryLength = 0;
    
    // Clean up hidden elements
    this.removeHiddenVideoElement();
    this.removeHiddenAudioElement();
    
    // Reset session state so next open creates a new session
    this.resetSessionState();
    this.setStatus('idle');
  }

  // ============================================================================
  // Session Refresh
  // ============================================================================

  private scheduleRefresh(): void {
    this.clearRefreshTimer();

    if (!this.sessionState.expires_at) {
      console.log('[AriaSessionManager] No expiration time, skipping refresh scheduling');
      return;
    }

    const expiresAt = new Date(this.sessionState.expires_at).getTime();
    const now = Date.now();
    const bufferMs = this.refreshConfig.bufferMinutes * 60 * 1000;
    const refreshAt = expiresAt - bufferMs;
    const delay = Math.max(0, refreshAt - now);
    const timeToExpiry = expiresAt - now;

    console.log(`[AriaSessionManager] Token expires at: ${new Date(expiresAt).toISOString()}`);
    console.log(`[AriaSessionManager] Time until expiry: ${Math.round(timeToExpiry / 1000 / 60)} minutes`);
    console.log(`[AriaSessionManager] Refresh buffer: ${this.refreshConfig.bufferMinutes} minutes`);

    if (delay <= 0) {
      // Already past refresh time, refresh immediately
      console.log('[AriaSessionManager] Session expiring soon, refreshing immediately');
      this.refreshSession();
    } else {
      console.log(`[AriaSessionManager] Scheduling proactive refresh in ${Math.round(delay / 1000 / 60)} minutes`);
      this.refreshTimer = setTimeout(() => {
        console.log('[AriaSessionManager] Proactive refresh timer fired');
        this.refreshSession();
      }, delay);
    }
  }

  async refreshSession(): Promise<void> {
    // Check tab visibility
    if (this.refreshConfig.refreshOnlyWhenVisible && !this.isTabVisible) {
      console.log('[AriaSessionManager] Tab not visible, deferring refresh');
      this.pendingRefresh = true;
      return;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[AriaSessionManager] Cannot refresh: WebSocket not connected');
      // Try to reconnect if not connected
      if (!this.isIntentionalDisconnect && !this.isPaused) {
        this.connect();
      }
      return;
    }

    if (this.sessionState.isRefreshing) {
      console.log('[AriaSessionManager] Refresh already in progress');
      return;
    }

    console.log('[AriaSessionManager] Refreshing session token (proactive refresh)');
    this.updateSessionState({ isRefreshing: true });
    this.setStatus('refreshing');

    // Always skip greeting on refresh since user has already had a session
    const shouldSkipGreeting = this.hasHadSessionBefore;

    try {
      // Use user_trigger: true with userId to get a fresh valid session token
      // Include skip_greeting if user has had a session before
      const message = JSON.stringify({ 
        user_trigger: true,
        userId: this.config.userId,
        ...(shouldSkipGreeting && { skip_greeting: true })
      });
      this.ws.send(message);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh session';
      console.error('[AriaSessionManager] Refresh error:', errorMessage);
      this.updateSessionState({ isRefreshing: false });
      this.config.onSessionRefreshError?.(err instanceof Error ? err : new Error(errorMessage));
      this.scheduleRetry();
    }
  }

  // ============================================================================
  // Retry Logic
  // ============================================================================

  private scheduleRetry(): void {
    if (this.retryCount >= this.refreshConfig.maxRetries) {
      console.error('[AriaSessionManager] Max retries reached');
      this.setStatus('error');
      this.updateSessionState({ lastError: 'Failed to get session token after max retries' });
      return;
    }

    const delay = this.refreshConfig.baseRetryDelay * Math.pow(2, this.retryCount);
    this.retryCount++;

    console.log(`[AriaSessionManager] Scheduling retry ${this.retryCount}/${this.refreshConfig.maxRetries} in ${delay}ms`);

    this.retryTimer = setTimeout(() => {
      if (this.isConnected()) {
        console.log('[AriaSessionManager] Retrying session token request');
        this.requestSessionToken();
      } else {
        console.log('[AriaSessionManager] Reconnecting WebSocket for retry');
        this.connect();
      }
    }, delay);
  }

  // ============================================================================
  // Tab Visibility
  // ============================================================================

  private setupVisibilityListener(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  private handleVisibilityChange = (): void => {
    this.isTabVisible = document.visibilityState === 'visible';

    if (this.isTabVisible && this.pendingRefresh) {
      console.log('[AriaSessionManager] Tab visible, executing pending refresh');
      this.pendingRefresh = false;
      this.refreshSession();
    }
  };

  // ============================================================================
  // Session Persistence (beforeunload handler)
  // ============================================================================

  private setupBeforeUnloadHandler(): void {
    if (typeof window !== 'undefined') {
      this.beforeUnloadHandler = () => {
        // Always end session when tab/window closes
        if (this.sessionState.session_id) {
          console.log('[AriaSessionManager] Tab closing, ending session');
          this.sendSessionEnd();
        }
      };
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }
  }

  private removeBeforeUnloadHandler(): void {
    if (typeof window !== 'undefined' && this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }

  // ============================================================================
  // Session Pause/Resume (for persistSession feature)
  // ============================================================================

  /**
   * Check if session persistence is enabled
   * Returns FALSE - sessions are ended when widget closes to save Anam resources
   * A new session with skip_greeting is created when widget reopens
   */
  isPersistSessionEnabled(): boolean {
    // Always false - closing widget ends session, reopening creates new one with skip_greeting
    return false;
  }

  /**
   * Check if WebSocket should stay alive when paused
   */
  shouldKeepWebSocketAlive(): boolean {
    // Default to true if persistSession is enabled
    return this.config.keepWebSocketAlive !== false;
  }

  /**
   * Check if widget is closed (session was ended on close)
   * Note: With the new behavior, sessions are ended when widget closes,
   * not paused. This returns true if the widget was closed.
   */
  isSessionPaused(): boolean {
    // Returns true if we don't have an active Anam client (session was ended)
    return !this.anamClient && this.hasHadSessionBefore;
  }

  /**
   * Pause the session (called when widget closes / user hangs up)
   * 
   * NEW BEHAVIOR: Ends the Anam session to save resources.
   * When widget reopens, a new session is created with skip_greeting: true
   * so the greeting doesn't play again.
   * 
   * This is more resource-efficient than keeping the session alive.
   */
  async pauseSession(): Promise<void> {
    // Only end if we have an active Anam client
    if (!this.anamClient) {
      console.log('[AriaSessionManager] No active Anam client, nothing to pause/end');
      return;
    }

    console.log('[AriaSessionManager] Widget closed - ending Anam session (will skip greeting on reopen)');
    
    // End the current session - this frees up Anam resources
    // The next session will have skip_greeting: true since hasHadSessionBefore is set
    await this.endCurrentSession();

    this.config.onSessionPaused?.();
  }

  /**
   * Resume the session (called when widget reopens)
   * 
   * Since we end the session on pause (to free Anam resources), this requests a new
   * session token with skip_greeting: true, then initializes the Anam client.
   * 
   * @returns true if session is ready or initialization started, false if waiting for token
   */
  async resumeSession(): Promise<boolean> {
    console.log('[AriaSessionManager] Widget reopened - will create new session with skip_greeting');
    
    // Reset intentional disconnect flag since user is reopening
    this.isIntentionalDisconnect = false;

    // If we already have an active Anam client, we're good
    if (this.anamClient && this.videoStream) {
      console.log('[AriaSessionManager] Active session exists, no need to create new one');
      this.setStatus('ready');
      this.config.onSessionResumed?.();
      return true;
    }

    // Check if we have a preloaded session token ready
    if (this.sessionState.token && !this.isTokenExpired()) {
      console.log('[AriaSessionManager] Session token ready, initializing Anam client (skip_greeting applied)');
      try {
        await this.initializeAnamClient(this.sessionState.token);
        this.config.onSessionResumed?.();
        return true;
      } catch (err) {
        console.error('[AriaSessionManager] Failed to initialize session on resume:', err);
        // Token might be invalid, request a new one
        this.updateSessionState({ token: null, isSessionReady: false });
      }
    }

    // No valid token, need to request one (will include skip_greeting: true)
    if (this.isConnected()) {
      console.log('[AriaSessionManager] Requesting new session token for resume');
      this.requestSessionToken();
    } else {
      console.log('[AriaSessionManager] WebSocket not connected, reconnecting...');
      this.connect();
    }

    // Return false to indicate session is being created asynchronously
    // The caller should wait for session_ready event
    return false;
  }

  /**
   * Reset session state (used when session expires or needs to be recreated)
   */
  private resetSessionState(): void {
    this.sessionState = {
      session_id: null,
      token: null,
      expires_at: null,
      isSessionReady: false,
      lastError: null,
      isPreloaded: false,
      isRefreshing: false,
    };
    this.isPaused = false;
  }

  /**
   * Force end the session (explicit user action or logout)
   * This always ends the session regardless of persistSession setting
   * 
   * @param resetGreeting - If true, resets the greeting state so next session shows greeting (useful for logout)
   */
  async forceEndSession(resetGreeting: boolean = false): Promise<void> {
    console.log('[AriaSessionManager] Force ending session', resetGreeting ? '(resetting greeting state)' : '');
    
    // Send session end event
    this.sendSessionEnd();
    
    // Stop streaming and cleanup
    await this.stopStreaming();
    
    // Reset state
    this.resetSessionState();
    this.isPaused = false;
    
    // Optionally reset greeting state (for logout scenarios)
    if (resetGreeting) {
      this.hasHadSessionBefore = false;
      console.log('[AriaSessionManager] Greeting state reset - next session will show greeting');
    }
    
    this.setStatus('idle');
  }

  /**
   * Reset the greeting state so the next session will show the greeting
   * Call this when user logs out or you want to show greeting again
   */
  resetGreetingState(): void {
    this.hasHadSessionBefore = false;
    console.log('[AriaSessionManager] Greeting state reset - next session will show greeting');
  }

  /**
   * Check if greeting will be skipped on next session
   */
  willSkipGreeting(): boolean {
    return this.hasHadSessionBefore;
  }

  // ============================================================================
  // Auto-Reconnect on Anam Disconnect (Inactivity Timeout)
  // ============================================================================

  /**
   * Check if the session is currently reconnecting
   */
  isSessionReconnecting(): boolean {
    return this.isReconnecting;
  }

  /**
   * Enable or disable auto-reconnect when Anam disconnects (e.g., due to inactivity)
   */
  setAutoReconnectOnAnamDisconnect(enabled: boolean): void {
    this.autoReconnectOnAnamDisconnect = enabled;
  }

  /**
   * Auto-reconnect the session after Anam disconnects (e.g., due to inactivity timeout)
   * This creates a new session seamlessly without user intervention
   */
  private async autoReconnectSession(): Promise<void> {
    if (this.isReconnecting) {
      console.log('[AriaSessionManager] Already reconnecting, skipping');
      return;
    }

    console.log('[AriaSessionManager] Starting auto-reconnect...');
    this.isReconnecting = true;
    this.emit({ type: 'reconnecting', data: { isReconnecting: true } });

    try {
      // Clean up old session (but don't send session_end - Anam connection already closed)
      await this.cleanupForReconnect();

      // Ensure WebSocket is connected
      if (!this.isConnected()) {
        console.log('[AriaSessionManager] WebSocket not connected, reconnecting...');
        this.connect();
        // Wait for WebSocket to connect (the session token request happens automatically)
        return; // handleSessionResponse will continue the flow
      }

      // Request new session token
      console.log('[AriaSessionManager] Requesting new session token for reconnect');
      this.requestSessionToken();
      
      // The handleSessionResponse will receive the token and we'll initialize from there
    } catch (err) {
      console.error('[AriaSessionManager] Auto-reconnect failed:', err);
      this.isReconnecting = false;
      this.emit({ type: 'reconnecting', data: { isReconnecting: false } });
      this.setStatus('error');
      this.updateSessionState({ lastError: 'Failed to reconnect session' });
      this.config.onError?.(err instanceof Error ? err : new Error('Failed to reconnect session'));
    }
  }

  /**
   * Clean up resources for reconnection (without sending session_end)
   */
  private async cleanupForReconnect(): Promise<void> {
    console.log('[AriaSessionManager] Cleaning up for reconnect');

    // Stop Anam client without sending session_end (connection already closed)
    if (this.anamClient) {
      try {
        await this.anamClient.stopStreaming();
      } catch (err) {
        // Ignore - connection already closed
        console.log('[AriaSessionManager] Anam stopStreaming error (expected):', err);
      }
      this.anamClient = null;
    }

    // Stop audio input
    if (this.audioInputStream) {
      this.audioInputStream.getTracks().forEach(track => track.stop());
      this.audioInputStream = null;
    }

    // Clear video stream
    this.videoStream = null;

    // Remove hidden elements (will be recreated)
    this.removeHiddenVideoElement();
    this.removeHiddenAudioElement();

    // Clear chat state for fresh session
    this.chatMessages = [];
    this.liveTranscript = '';
    this.lastMessageHistory = [];
    this.typedUserMessages = [];
    this.anamHistoryLength = 0;

    // Clear tool call state
    this.clearToolCallState();

    // Reset session state for new session (keep WebSocket connected)
    this.updateSessionState({
      session_id: null,
      token: null,
      expires_at: null,
      isSessionReady: false,
      lastError: null,
      isPreloaded: false,
      isRefreshing: false,
    });

    this.isAnamInitializing = false;
  }

  /**
   * Clear stale session data when Anam disconnects while paused
   * This ensures a fresh session is created when the widget reopens
   */
  private clearStaleSessionForResume(): void {
    console.log('[AriaSessionManager] Clearing stale session for resume');

    // Clear Anam client reference (already disconnected)
    this.anamClient = null;

    // Clear video/audio streams
    this.videoStream = null;
    if (this.audioInputStream) {
      this.audioInputStream.getTracks().forEach(track => track.stop());
      this.audioInputStream = null;
    }

    // Remove hidden elements
    this.removeHiddenVideoElement();
    this.removeHiddenAudioElement();

    // Clear the stale token so a new one is requested on resume
    this.updateSessionState({
      session_id: null,
      token: null,
      isSessionReady: false,
      isPreloaded: false,
    });

    // Reset initialization flag
    this.isAnamInitializing = false;

    // Keep isPaused = true so resumeSession flow is triggered
    // Keep WebSocket connected for faster reconnection
    console.log('[AriaSessionManager] Stale session cleared, ready for fresh session on resume');
  }

  /**
   * End the current session but keep WebSocket connected for new session creation
   * This is called when widget closes without persistSession enabled
   * The next time the widget opens, a new session will be created
   */
  async endCurrentSession(): Promise<void> {
    console.log('[AriaSessionManager] Ending current session (keeping WebSocket alive)');
    
    // Mark as intentional disconnect to prevent auto-reconnect when Anam connection closes
    this.isIntentionalDisconnect = true;
    
    // Send session end event to backend
    this.sendSessionEnd();
    
    // Stop the Anam client and streaming
    if (this.anamClient) {
      try {
        await this.anamClient.stopStreaming();
      } catch (err) {
        console.error('[AriaSessionManager] Error stopping streaming:', err);
      }
      this.anamClient = null;
    }

    // Stop audio input
    if (this.audioInputStream) {
      this.audioInputStream.getTracks().forEach(track => track.stop());
      this.audioInputStream = null;
    }

    // Clear video stream
    this.videoStream = null;
    
    // Clear chat state
    this.chatMessages = [];
    this.liveTranscript = '';
    this.lastMessageHistory = [];
    this.typedUserMessages = [];
    this.anamHistoryLength = 0;
    
    // Clear tool call state
    this.clearToolCallState();
    
    // Reset session state but keep WebSocket connected
    // This allows a new session to be created when widget reopens
    this.updateSessionState({
      session_id: null,
      token: null,
      expires_at: null,
      isSessionReady: false,
      lastError: null,
      isPreloaded: false,
      isRefreshing: false,
    });
    
    this.isPaused = false;
    this.isAnamInitializing = false;
    
    // Set status to connected (WebSocket is still alive) rather than idle
    // Don't request new token here - wait until widget reopens
    if (this.isConnected()) {
      this.setStatus('connected');
      console.log('[AriaSessionManager] Session ended, ready for new session when widget reopens');
    } else {
      this.setStatus('idle');
    }
  }

  // ============================================================================
  // Cleanup
  // ============================================================================

  private clearTimers(): void {
    this.clearRefreshTimer();
    this.clearReconnectTimer();
    this.clearRetryTimer();
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private clearRetryTimer(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  /**
   * Set callback for WebSocket trigger events
   */
  setWebSocketTriggerCallback(callback: ((event: WebSocketTriggerEvent) => void) | null): void {
    this.onWebSocketTrigger = callback;
  }

  /**
   * Destroy the session manager and clean up all resources
   */
  destroy(): void {
    console.log('[AriaSessionManager] Destroying instance');

    // Send session end if active
    if (this.sessionState.session_id) {
      this.sendSessionEnd();
    }

    // Stop streaming (this also removes hidden elements)
    this.stopStreaming();

    // Remove hidden elements (in case stopStreaming didn't run)
    this.removeHiddenVideoElement();
    this.removeHiddenAudioElement();

    // Clear timers
    this.clearTimers();

    // Disconnect WebSocket
    this.disconnect();

    // Remove visibility listener
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    // Remove beforeunload handler
    this.removeBeforeUnloadHandler();

    // Clear listeners
    this.listeners.clear();

    // Reset state
    this.resetSessionState();
  }
}

export default AriaSessionManager;
